import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ConfigProvider } from 'antd';
import { BrowserRouter } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const isExternalThemePath = (pathname: string, search?: string) => {
  if (pathname.startsWith('/external-projects') || pathname.includes('/ext/')) return true;
  if (pathname === '/results' && search && new URLSearchParams(search).get('scope') === 'External') return true;
  return false;
};

const getPathnameFromHistoryUrl = (url?: string | URL | null) => {
  if (!url) return window.location.pathname;
  try {
    if (typeof url === 'string') {
      return new URL(url, window.location.origin).pathname;
    }
    return url.pathname || window.location.pathname;
  } catch {
    return window.location.pathname;
  }
};

const getSearchFromHistoryUrl = (url?: string | URL | null) => {
  if (!url) return window.location.search;
  try {
    if (typeof url === 'string') {
      return new URL(url, window.location.origin).search;
    }
    return url.search ?? window.location.search;
  } catch {
    return window.location.search;
  }
};

const applyThemeClassForPath = (pathname: string, search?: string) => {
  if (typeof window === 'undefined') return;
  const isExternal = isExternalThemePath(pathname, search ?? window.location.search);
  document.documentElement.classList.toggle('external-theme', isExternal);
  if (document?.body) {
    document.body.classList.toggle('external-theme', isExternal);
  }
};

const syncThemeClassWithUrl = () => {
  if (typeof window === 'undefined' || !document?.body) return;
  applyThemeClassForPath(window.location.pathname, window.location.search);
};

// Global error handler - auth error'larını gizle
const originalConsoleError = console.error;
console.error = (...args) => {
  // Auth error'larını gizle
  const errorMessage = args.join(' ');
  if (errorMessage.includes('401 (Unauthorized)') || 
      errorMessage.includes('net::ERR_ABORTED') ||
      errorMessage.includes('GET https://api.revisdom.com/api/User/me')) {
    return;
  }
  // Diğer error'ları normal şekilde göster
  originalConsoleError.apply(console, args);
};

// Global warn handler - React Router uyarılarını da gizle
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  const warnMessage = args.join(' ');
  if (warnMessage.includes('React Router Future Flag Warning')) {
    return;
  }
  // Diğer uyarıları normal şekilde göster
  originalConsoleWarn.apply(console, args);
};

const setInitialSidebarClass = () => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    const isCollapsed = saved === 'true';
    document.documentElement.classList.toggle('sidebar-collapsed', isCollapsed);
  } catch {}
};

setInitialSidebarClass();

// React render'ını beklemeden URL'ye göre tema class'ını uygula
syncThemeClassWithUrl();

// Route değişimlerinde class'ı anında güncelle (flick'i engeller)
const originalPushState = window.history.pushState.bind(window.history);
window.history.pushState = ((...args: Parameters<History['pushState']>) => {
  const nextPathname = getPathnameFromHistoryUrl(args[2]);
  const nextSearch = getSearchFromHistoryUrl(args[2]);
  applyThemeClassForPath(nextPathname, nextSearch);
  originalPushState(...args);
  syncThemeClassWithUrl();
}) as History['pushState'];

const originalReplaceState = window.history.replaceState.bind(window.history);
window.history.replaceState = ((...args: Parameters<History['replaceState']>) => {
  const nextPathname = getPathnameFromHistoryUrl(args[2]);
  const nextSearch = getSearchFromHistoryUrl(args[2]);
  applyThemeClassForPath(nextPathname, nextSearch);
  originalReplaceState(...args);
  syncThemeClassWithUrl();
}) as History['replaceState'];

window.addEventListener('popstate', () => {
  syncThemeClassWithUrl();
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ConfigProvider>
      <BrowserRouter>
        <LanguageProvider>
          <App />
        </LanguageProvider>
      </BrowserRouter>
    </ConfigProvider>
  </React.StrictMode>
);

// Service worker (push bildirimleri için) — sadece production build'de kayıt yapılır
serviceWorkerRegistration.register();
